import type { ModelKind } from './ModelPicker'

/** Перерисовывать ли слой вероятности при смене вида / глубины / даты.
 *
 *  URL тайла вероятности — `probability_{depth}` по конкретному ВИДУ и ДАТЕ
 *  (`probabilityTileUrl(species, date, depth)`), поэтому смена любого из трёх
 *  входов должна переставлять URL слоя. Но только для probability-модели (HSI)
 *  и ТОЛЬКО когда её слой уже показан (owner совпал): иначе смена вида/глубины
 *  автопоказала бы слой без нажатия «Загрузить». Течения и апвеллинг обновляются
 *  своим фоновым эффектом (там входы уже в зависимостях).
 */
export function shouldRepointProbabilityLayer(
  model: ModelKind,
  shownOwner: ModelKind | null,
): boolean {
  if (model !== 'hsi') return false
  return shownOwner === model
}

/** Ответ `/api/v1/tiles/raster/status/{species}/{date}`: что уже нарисовано. */
export interface RasterStatus {
  status?: string
  available_layers?: Array<{ layer_depth?: string; tile_count?: number }>
  /** Метка последней отрисовки тайлов (см. probabilityTileUrl). */
  tiles_version?: string | null
}

/**
 * Показывать ли поле вероятности сразу при входе в модель, без «Загрузить».
 *
 * ПОЧЕМУ АВТОПОКАЗ ВООБЩЕ ПОЯВИЛСЯ. Слой вероятности показывался исключительно
 * кнопкой в самом HSI-виде, и переключение на HSI давало серую карту — при том
 * что на сервере всё готово, а траектории по HSI поверх этой серости рисовались
 * (они читают поле прогноза напрямую, минуя тайлы). Пользователь видел
 * доказательство существования поля и пустую карту рядом с ним.
 *
 * Условие показа — не «прогноз есть», а «есть ЧЕМ красить ИМЕННО ЭТОТ срез»:
 * нужная глубина среди готовых слоёв и непустой счётчик тайлов. Иначе пустая
 * карта выдавалась бы за готовую — ровно та ошибка, от которой уводит
 * разделение «данные есть» и «тайлы готовы».
 */
export function probabilityAutoShow(
  status: RasterStatus | null | undefined,
  depth: number,
): boolean {
  if (!status || status.status !== 'ready') return false
  const layer = (status.available_layers ?? []).find(
    l => l.layer_depth === `probability_${depth}`,
  )
  return !!layer && (layer.tile_count ?? 0) > 0
}
